import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { Maybe } from '__generated__/graphql-types-do-not-use';

import GetCourseBySlugQuery from 'bundles/course-v2/queries/GetCourseBySlugQuery.graphql';
import type {
  GetCourseBySlugQueryQueryVariables,
  GetCourseBySlugQueryQuery as GetCourseBySlugQueryType,
} from 'bundles/course-v2/queries/__generated__/GetCourseBySlugQuery';

// Aligned with GraphQL response shape
export type CourseResponseTypeMetadata = NonNullable<
  NonNullable<GetCourseBySlugQueryType['Course']>['queryBySlug']
>['courseTypeMetadata'];

export type CourseData = {
  courseId: string;
  courseSlug: string;
  courseName: string;
  courseTypeMetadata: CourseResponseTypeMetadata | null;
  launchedAt: Maybe<string> | undefined;
  isReal: boolean;
};

type CourseBySlugResult = {
  courseData: CourseData | undefined;
  loading: boolean;
  error: Error | undefined;
};

/**
 * Fetches basic course data by slug - replacement for loadCourseV1
 * @param slug Course slug
 * @returns Course data including ID, slug, courseTypeMetadata, and launch status
 */
const useCourseBySlug = (slug: string): CourseBySlugResult => {
  const { data, loading, error } = useQuery<GetCourseBySlugQueryType, GetCourseBySlugQueryQueryVariables>(
    GetCourseBySlugQuery,
    {
      variables: {
        slug,
      },
      context: { clientName: 'gatewayGql' },
      skip: !slug,
    }
  );

  const courseData = useMemo(() => {
    const result = data?.Course?.queryBySlug;
    if (!result) {
      return undefined;
    }

    return {
      courseId: result.id,
      courseSlug: result.slug,
      courseName: result.name,
      courseTypeMetadata: result.courseTypeMetadata,
      launchedAt: result.launchedAt,
      isReal: result.isReal,
    };
  }, [data]);

  return {
    courseData,
    loading,
    error,
  };
};

export default useCourseBySlug;
